import React, { useEffect, useState } from 'react';
import { Sparkles, Lock, AlertCircle } from 'lucide-react';
import { colorChangeAPI } from '../api';

/**
 * Inline section that lets the partner enable AI Color Change on a product.
 * Renders inline help, a quality picker (Standard / Premium), and the
 * partner's per-call price for both qualities.
 *
 * Hidden behind a "≥ 2 color variants required" gate. When fewer variants
 * are present, the toggle is disabled with an explanatory message.
 *
 * Props:
 *   variantCount: number — used to gate the toggle
 *   enabled: boolean
 *   quality: 'standard' | 'premium'
 *   onChange({ enabled, quality })
 */
const ColorChangeSection = ({ variantCount, enabled, quality, onChange }) => {
  const [costs, setCosts] = useState({ standard: null, premium: null, plan: '' });
  const meetsMin = (variantCount || 0) >= 2;

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      colorChangeAPI.getCost('standard'),
      colorChangeAPI.getCost('premium'),
    ])
      .then(([s, p]) => {
        if (cancelled) return;
        setCosts({
          standard: s.data?.credits_per_call,
          premium: p.data?.credits_per_call,
          plan: s.data?.plan,
        });
      })
      .catch(() => { /* non-fatal — UI still works */ });
    return () => { cancelled = true; };
  }, []);

  // Emit the canonical product-state keys so every parent can simply spread
  // `...patch` into its product object without needing a translator.
  // (Earlier this emitted `{enabled, quality}` which silently created NEW
  //  fields on the parent's product instead of toggling color_change_*.)
  const set = (patch) => {
    const next = { enabled, quality, ...patch };
    onChange({
      color_change_enabled: !!next.enabled,
      color_change_quality: next.quality || 'standard',
    });
  };

  return (
    <div
      className="rounded-xl border border-slate-200 bg-gradient-to-br from-violet-50/60 to-pink-50/60 p-4 space-y-3"
      data-testid="color-change-section"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900">AI Color Change</h4>
            <p className="text-xs text-slate-600 mt-0.5">
              Let shoppers see this dress in any of its color variants — instantly,
              right after Try-On.
            </p>
          </div>
        </div>

        {/* Toggle */}
        <label
          className={`relative inline-flex items-center cursor-pointer flex-shrink-0 ${meetsMin ? '' : 'opacity-50 cursor-not-allowed'}`}
          data-testid="color-change-toggle"
        >
          <input
            type="checkbox"
            className="sr-only peer"
            checked={!!enabled && meetsMin}
            disabled={!meetsMin}
            onChange={(e) => set({ enabled: e.target.checked })}
            data-testid="color-change-toggle-input"
          />
          <div className="w-11 h-6 bg-slate-200 peer-checked:bg-gradient-to-r peer-checked:from-violet-500 peer-checked:to-pink-500 rounded-full peer-disabled:bg-slate-200 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:shadow-sm after:transition-transform peer-checked:after:translate-x-5" />
        </label>
      </div>

      {!meetsMin && (
        <div
          className="flex items-start gap-2 text-xs text-slate-600 bg-white/60 rounded-md px-2.5 py-1.5"
          data-testid="color-change-min-variants-warning"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
          <span>Add at least 2 color variants below to enable AI Color Change.</span>
        </div>
      )}

      {meetsMin && enabled && (
        <div className="grid grid-cols-2 gap-2.5" data-testid="color-change-quality-picker">
          <QualityCard
            id="standard"
            label="Standard"
            price={costs.standard}
            description="Fast, on-device. Best for clean color swaps."
            active={quality === 'standard'}
            onClick={() => set({ quality: 'standard' })}
          />
          <QualityCard
            id="premium"
            label="Premium"
            price={costs.premium}
            description="Gemini-powered. Handles tie-dye, multi-tone, metallics."
            active={quality === 'premium'}
            onClick={() => set({ quality: 'premium' })}
            badge="Pro"
          />
        </div>
      )}

      {meetsMin && enabled && (
        <p className="text-[11px] text-slate-500 leading-snug flex items-start gap-1.5">
          <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
          Cached repeats (same color, same try-on) are <b>free</b>. You're only
          charged for the first time a shopper picks each color combo.
        </p>
      )}
    </div>
  );
};

const QualityCard = ({ id, label, price, description, active, onClick, badge }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left rounded-lg border p-2.5 transition-all relative ${
      active
        ? 'border-violet-500 bg-white shadow-sm ring-1 ring-violet-500'
        : 'border-slate-200 bg-white/50 hover:border-slate-300 hover:bg-white'
    }`}
    data-testid={`color-change-quality-${id}`}
  >
    <div className="flex items-center justify-between mb-1">
      <span className={`text-sm font-semibold ${active ? 'text-violet-700' : 'text-slate-900'}`}>{label}</span>
      {badge && (
        <span className="text-[9px] uppercase tracking-wider font-bold text-violet-600 bg-violet-100 rounded px-1.5 py-0.5">
          {badge}
        </span>
      )}
    </div>
    <p className="text-[11px] text-slate-600 leading-tight mb-1.5">{description}</p>
    <div className="text-xs font-medium text-slate-900">
      {price !== null && price !== undefined
        ? <><span className="tabular-nums">{price}</span> credit{price === 1 ? '' : 's'}<span className="text-slate-400 font-normal"> / change</span></>
        : <span className="text-slate-400">…</span>}
    </div>
  </button>
);

export default ColorChangeSection;
